import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ArrowUp } from "lucide-react";
import { RazorInputField } from "../../../../components/razor-reuse";
import type { AppDispatch } from "../../../../redux/store";
import RootState from "../../../../redux/type";
import { formatDateToYYYYMMDD } from "../../../../utils/Helper";
import {
  exportRateComparisonCsv,
  getComparisonCurrencyPairOptions,
  getRateHistory,
} from "../../../../redux/rateIntelligence";
import { HISTORY_PROVIDER_ORDER, PERIOD_OPTIONS } from "../types";
import RateComparisonTable from "./RateComparisonTable";

const ALL_PAIRS_OPTION = { label: "All pairs", value: "" };

const POSITION_OPTIONS = [
  { label: "All positions", value: "" },
  { label: "Leading", value: "leading" },
  { label: "Behind", value: "behind" },
];

export default function HistoryTab() {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const canExport = !!user?.permissions?.includes("export_rate_comparison");
  const {
    history_pairs,
    history_quoted_at,
    history_meta,
    comparison_currency_pair_options,
    loading_history,
  } = useSelector((state: RootState) => state.rate_intelligence);

  const currencyPairOptions = useMemo(
    () => [ALL_PAIRS_OPTION, ...(comparison_currency_pair_options || [])],
    [comparison_currency_pair_options],
  );

  const [currencyPair, setCurrencyPair] = useState(ALL_PAIRS_OPTION);
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
  const [customDate, setCustomDate] = useState("");
  const [position, setPosition] = useState(POSITION_OPTIONS[0]);
  const [page, setPage] = useState(1);

  const isCustomPeriod = period.value === "custom";

  // Populates the "Currency pair" dropdown from the full live dataset,
  // independent of the filtered/paginated page fetched below.
  useEffect(() => {
    dispatch(getComparisonCurrencyPairOptions() as any);
  }, [dispatch]);

  useEffect(() => {
    // A custom period with no date picked yet has nothing to fetch for.
    if (isCustomPeriod && !customDate) return;

    dispatch(
      getRateHistory({
        currency_pair: currencyPair.value,
        period: period.value,
        custom_date: isCustomPeriod ? customDate : undefined,
        position: position.value as any,
        page,
      }) as any,
    );
  }, [dispatch, currencyPair, period, customDate, isCustomPeriod, position, page]);

  const handleExport = () => {
    // A custom period with no date picked yet has nothing to export for —
    // same guard as the table's own fetch above.
    if (isCustomPeriod && !customDate) return;

    dispatch(
      exportRateComparisonCsv({
        currency_pair: currencyPair.value,
        period: period.value,
        custom_date: isCustomPeriod ? customDate : undefined,
        position: position.value as any,
        fileLabel: "rate history report",
      }) as any,
    );
  };

  return (
    <div className="mt-[16px]">
      <div className="flex items-end justify-between gap-[16px] flex-wrap">
        <div className="flex items-end gap-[16px] flex-wrap">
          <div className="w-[200px]">
            <RazorInputField
              color="black-light"
              type="select"
              label="Currency pair"
              value={currencyPair}
              onChange={(e: any) => {
                setCurrencyPair(e);
                setPage(1);
              }}
              selectOption={currencyPairOptions}
            />
          </div>
          <div className="w-[200px]">
            <RazorInputField
              color="black-light"
              type="select"
              label="Period"
              value={period}
              onChange={(e: any) => {
                setPeriod(e);
                setCustomDate("");
                setPage(1);
              }}
              selectOption={PERIOD_OPTIONS}
            />
          </div>
          {isCustomPeriod && (
            <div className="w-[200px]">
              <RazorInputField
                color="black-light"
                type="date"
                label="Custom date"
                placeholder="Select date"
                value={customDate}
                dateOptions={{ maxDate: new Date() }}
                onChange={(e: any) => {
                  setCustomDate(formatDateToYYYYMMDD(e[0]));
                  setPage(1);
                }}
              />
            </div>
          )}
          <div className="w-[200px]">
            <RazorInputField
              color="black-light"
              type="select"
              label="Position"
              value={position}
              onChange={(e: any) => {
                setPosition(e);
                setPage(1);
              }}
              selectOption={POSITION_OPTIONS}
            />
          </div>
        </div>
        {canExport && (
          <button
            type="button"
            onClick={handleExport}
            className="flex h-[40px] items-center gap-[8px] rounded-[8px] border border-[#E4E4E7] px-[16px] text-[14px] text-[#1E1E1E] hover:bg-[#F7F7F7]"
          >
            <ArrowUp className="size-[14px]" />
            Export
          </button>
        )}
      </div>

      <div className="mt-[16px]">
        <RateComparisonTable
          pairs={history_pairs || []}
          providers={HISTORY_PROVIDER_ORDER}
          quotedAt={history_quoted_at}
          loading={loading_history}
          currentPage={history_meta?.current_page || page}
          perPage={history_meta?.per_page || 10}
          totalCount={history_meta?.total || 0}
          onPageChange={(num) => setPage(num)}
        />
      </div>
    </div>
  );
}
